import { landingFolderRestrictionApi } from '@data-delivery/permission-ui-api';

import type {
  CreateLandingFolderRestrictionDto,
  LFREntity,
  UpdateLandingFolderRestrictionDto,
} from '@data-delivery/permission-ui-api/';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { configurationKeys } from './configuration-keys';

const landingFolderRestrictionsQuery = () => ({
  queryKey: configurationKeys.landingFolderRestrictions(),
  async queryFn() {
    const { data } = await landingFolderRestrictionApi.getAllLandingFolders();
    return data ?? [];
  },
});

const lFRByIdQuery = (id: string | undefined) => ({
  enabled: Boolean(id),
  queryKey: configurationKeys.landingFolderRestriction(id ?? ''),
  async queryFn() {
    if (id) {
      const { data } = await landingFolderRestrictionApi.getLandingFolderRestrictionById({ id });
      return data;
    }
  },
});

const lFRByTypeQuery = () => ({
  queryKey: configurationKeys.configurationsByType('landingFolderRestrictions'),
  async queryFn() {
    const { data } = await landingFolderRestrictionApi.getAllLandingFolders();
    return data ?? [];
  },
});

export function useLandingFolderRestrictions() {
  return useQuery<LFREntity[]>({
    ...landingFolderRestrictionsQuery(),
  });
}

export function useLandingFolderRestrictionsByType() {
  return useQuery<LFREntity[]>({
    ...lFRByTypeQuery(),
  });
}

export function useLFRById(id: string | undefined) {
  return useQuery({
    ...lFRByIdQuery(id),
  });
}

export const useCreateLFR = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateLandingFolderRestrictionDto) =>
      landingFolderRestrictionApi
        .createLandingFolderRestriction({ createLandingFolderRestrictionDto: dto })
        .then(r => r.data),
    onSuccess() {
      return queryClient.invalidateQueries({
        queryKey: configurationKeys.configurationsByType('landingFolderRestrictions'),
      });
    },
  });
};

export const useUpdateLFR = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: UpdateLandingFolderRestrictionDto) =>
      landingFolderRestrictionApi
        .updateLandingFolderRestriction({ id, updateLandingFolderRestrictionDto: dto })
        .then(r => r.data),
    onSuccess(data) {
      queryClient.invalidateQueries({
        queryKey: configurationKeys.configurationsByType('landingFolderRestrictions'),
      });
      queryClient.invalidateQueries({
        queryKey: configurationKeys.landingFolderRestriction(id),
      });
      return data;
    },
  });
};

export const useDeleteLFR = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      landingFolderRestrictionApi.deleteLandingFolderRestriction({ id }).then(r => r.data),
    onSuccess(_, id) {
      queryClient.invalidateQueries({
        queryKey: configurationKeys.configurationsByType('landingFolderRestrictions'),
      });
      queryClient.invalidateQueries({
        queryKey: configurationKeys.landingFolderRestriction(id),
      });
    },
  });
};
